import {AppInitializeService} from '../app-initialize-service';
import {inject, Injectable} from '../di';
import {I18nService} from '../i18n/i18n-service';
import {Logger} from '../logger';
import {BehaviorSubject, skip} from 'rxjs';
import {Page, Rcu, Structure} from './structure';
import {ShopStructure} from './shop-structure';
import {getJson} from '../utils/get-json';
import {getText} from '../utils/get-text';
import {StructFilesInfo} from './struct-files-info';
import {ConfigService} from '../config/config-service';
import {LegacyLogService} from '../logger/legacy-log-service';

@Injectable()
export class StructService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly i18nService = inject(I18nService);
  private readonly configService = inject(ConfigService);
  private readonly legacyLogService = inject(LegacyLogService);

  public readonly contentUrl = 'c/';

  /**
   * Information about all content files, their size and hash.
   */
  private contentFilesInfo: StructFilesInfo | undefined;

  /**
   * List of available struct locales. It is obtained from the list of struct_[locale].json files in the content folder.
   */
  private availableStructLocales: string[] | undefined;

  /**
   * Cache for struct for different locales.
   */
  private structCache: Record<string, Structure> = {};

  /**
   * Cache for struct shop for different locales.
   */
  private structShopCache: Record<string, ShopStructure> = {};

  /**
   * Content struct object
   */
  public readonly structure$ = new BehaviorSubject<Structure | undefined>(undefined);

  public get structure() {
    return this.structure$.getValue()!;
  }

  /**
   * Hash of struct package (content). You could use it for cache-control of struct resources
   */
  public contentHash: string = Date.now().toString();

  constructor() {
    window.tv_content_url = this.contentUrl;
    window.structv2 = null;
    window.getPageFromStruct = this.getPageFromStruct.bind(this);
    window.getRcuPageFromStruct = this.getRcuPageFromStruct.bind(this);
  }

  public async init(): Promise<boolean> {
    try {
      // Get the file with the content hash, so we can use it for cache-control
      if (!(await this.initContentHash())) {
        return false;
      }

      // Get the list of actual content files, so we can check if they are available
      if (!(await this.initContentFiles())) {
        return false;
      }

      // Subscribe for locale changes and reload struct files accordingly
      this.i18nService.localeFallbackChain$.pipe(skip(1)).subscribe(() => void this.initStruct());

      return await this.initStruct();
    } catch (error) {
      this.logger.critical('Unable to load or initialize content (struct.json)', error);
      return false;
    }
  }

  public getPageFromStruct(id: string, initialItem?: Page): Page {
    id = typeof id === 'undefined' ? '' : id;
    id = id.toString().replace('#', '');
    id = id.indexOf('id_') !== -1 ? id : 'id_' + id;

    return this.structure.pages[id] || initialItem || {};
  }

  public getRcuPageFromStruct(id: string, initialItem?: Rcu): Rcu {
    id = typeof id === 'undefined' ? '' : id;
    id = id.toString().replace('#', '');
    id = id.indexOf('id_') !== -1 ? id : 'id_' + id;

    return this.structure.rcu[id] || initialItem || {};
  }

  private async initStruct(): Promise<boolean> {
    try {
      const structLocales = this.availableStructLocales;
      if (!structLocales || structLocales.length === 0) {
        this.logger.critical(`[StructService] Unable to load content, no struct locales was found`);
        return false;
      }

      const applicableLocales = this.i18nService.filterApplicableLocales(structLocales);
      if (applicableLocales.length === 0) {
        this.logger.critical(`[StructService] Unable to load content, no applicable content locales was found`);
        return false;
      }

      let structure: Structure | undefined = undefined;
      let structLocale: string | undefined = undefined;
      while (!structure && applicableLocales.length > 0) {
        structLocale = applicableLocales.shift()!;
        if (this.structCache[structLocale]) {
          this.logger.debug(`[StructService] Loading struct for ${structLocale} locale from local cache...`);
          structure = this.structCache[structLocale];
          break;
        }
        try {
          this.logger.debug(
            `[StructService] Loading struct for ${structLocale} locale from "struct_${structLocale}.json"...`,
          );

          // Check the content file size (it could be zero)
          if (this.contentFilesInfo?.struct?.[`struct_${structLocale}`]?.filesize === 0) {
            this.logger.error(
              `[StructService] Struct file for ${structLocale} locale from is empty! Skipping it and trying another locale...`,
            );
            continue;
          }

          structure = await this.getContentStructJson(structLocale);
          this.structCache[structLocale] = structure;
        } catch (err) {
          this.legacyLogService.add(`ERROR: JSON struct failed: ${structLocale}`); // TODO legacy logging
          this.logger.error(`[StructService] Unable to load content for ${structLocale} locale`, err);
          // continue downscaling the locale chain
        }
      }

      if (!structure) {
        this.logger.critical(`[StructService] Unable to load any content files for any suitable locale.`);
        return false;
      }

      structure = await this.initStructShop(structure, structLocale!);
      structure = this.changeCartItemLink(structure);
      structure = this.reorderStructLanguages(structure);
      structure = this.filterContentByDevice(structure);
      structure = this.insertGuestPlaceholders(structure);

      this.legacyLogService.add('got struct'); // TODO legacy logging
      window.structv2 = structure;
      this.structure$.next(structure);
    } catch (err) {
      this.logger.critical(`[StructService] Unexpected error while loading struct files`, err);
      window.tv_log(`No content to display`); // TODO legacy logging
      return false;
    }

    return true;
  }

  private async initStructShop(structure: Structure, locale: string) {
    try {
      // Check if the structShop file exists
      if (!this.contentFilesInfo?.struct?.[`structShop_${locale}.json`]) {
        this.logger.warn(`[StructService] "structShop_${locale}.json" file is absent, applying defaults`);
        this.applyStructShopFallbacks(structure);
        return structure;
      }

      // Check if the structShop file is not empty
      if (this.contentFilesInfo?.struct?.[`structShop_${locale}`]?.filesize === 0) {
        this.logger.warn(`[StructService] "structShop_${locale}.json" file is empty, applying defaults`);
        this.applyStructShopFallbacks(structure);
        return structure;
      }

      let structShop: ShopStructure;
      if (this.structShopCache[locale]) {
        this.logger.debug(`[StructService] Loading structShop for ${locale} locale from local cache...`);
        structShop = this.structShopCache[locale];
      } else {
        this.logger.debug(
          `[StructService] Loading structShop for ${locale} locale from "structShop_${locale}.json"...`,
        );
        structShop = await this.getStructShopJson(locale);
        this.structShopCache[locale] = structShop;
      }

      structure.pages = Object.assign({}, this.extendServicePage(structure.pages), structShop);
      structure.pages = this.extendPagesByWakeup(structure);
      return structure;
    } catch (err) {
      this.logger.error(`[StructService] Unable to load "structShop_${locale}.json", applying defaults`, err);
      this.applyStructShopFallbacks(structure);
      return structure;
    }
  }

  private applyStructShopFallbacks(structure: Structure) {
    structure.pages = this.extendServicePage(structure.pages);
    structure.pages = this.extendOldStructure(structure.pages);
    structure.pages = this.extendPagesByWakeup(structure);
  }

  private async initContentFiles(): Promise<boolean> {
    try {
      this.logger.debug(`[StructService] Getting filesv2.json file...`);
      const url = `${this.contentUrl}filesv2.json?hash=${this.contentHash}`;
      this.contentFilesInfo = await getJson<StructFilesInfo>(url);
      this.availableStructLocales = Object.entries(this.contentFilesInfo?.struct || {})
        .filter(([filename, info]) => info.filesize > 0 && filename.startsWith('struct_') && filename.endsWith('.json'))
        .map(([filename]) => filename.replace('struct_', '').replace('.json', ''));
      return true;
    } catch (err) {
      this.logger.critical(`[StructService] Unable to get content files list`, err);
      return false;
    }
  }

  private async initContentHash(): Promise<boolean> {
    this.logger.debug(`[StructService] Getting content.hash file...`);
    const url = `${this.contentUrl}content.hash?c=` + Date.now();
    try {
      this.contentHash = (await getText(url)) || this.contentHash;
    } catch (err) {
      this.logger.warn(`[StructService] Unable to get content hash on ${url}`, err);
      // We still can use random string for cache control, so it is not critical
      return true;
    }
    return true;
  }

  private getContentStructJson(locale: string): Promise<Structure> {
    const url = `${this.contentUrl}struct/struct_${locale}.json?hash=${this.contentHash}`;
    return getJson<Structure>(url);
  }

  private getStructShopJson(locale: string): Promise<ShopStructure> {
    const url = `${this.contentUrl}struct/structShop_${locale}.json?hash=${this.contentHash}`;
    return getJson<ShopStructure>(url);
  }

  private extendServicePage(pages: Structure['pages']): Structure['pages'] {
    for (const id in pages) {
      const page = pages[id];
      const serviceId = page.serviceId;

      if (serviceId) {
        const service = pages['id_' + serviceId];
        if (!service) {
          continue;
        }

        page.cost = service.cost;
        page.workingHours = service.workingHours;
        page.customField = service.customField;
        page.pieces = service.pieces;
        page.toppings = service.toppings;
        page.buttonName = service.buttonName;
        page.hasChoiceOfTime = typeof service.hasChoiceOfTime === 'undefined' ? true : service.hasChoiceOfTime;
      }
    }

    return pages;
  }

  private extendOldStructure(pages: Structure['pages']): Structure['pages'] {
    for (const id in pages) {
      const page = pages[id];
      if (page.type === 'shopCategory') {
        for (const childId in page.children) {
          if (page.children[childId].type === 'shopProduct') {
            pages[childId] = Object.assign({}, page.children[childId], pages[childId], {parentId: page.id});
          }
        }
      }
    }

    return pages;
  }

  private extendPagesByWakeup(structure: Structure): Structure['pages'] {
    if (typeof structure.wakeupcall === 'undefined') {
      return structure.pages;
    }

    const addedPages = {};
    addedPages.id_services_wakeup = structure.wakeupcall;
    addedPages.id_services_wakeup.id = 'services_wakeup';
    addedPages['id_' + structure.wakeupcall.serviceId] = structure.wakeupcall;

    return Object.assign({}, structure.pages, addedPages);
  }

  private changeCartItemLink(structure: Structure) {
    // TODO it seems it's not a place for such things

    // изменяем ссылку с #cart на #orders,
    // делаем так, чтобы сохранить обратную совместимость
    const menu = structure.menu;
    for (const id in menu) {
      const item = menu[id];
      if (item.link === '#cart') {
        item.link = '#orders';
        // item.title = isset('structv2.config.my_orders.title');
      }
    }
    return structure;
  }

  private reorderStructLanguages(structure: Structure) {
    const tmp_conf = this.configService.config?.tv?.settings_languages_order;

    if (tmp_conf && tmp_conf.length) {
      let out = [];
      for (let i = 0; i < tmp_conf.length; i++) {
        for (let o = structure.language.length - 1; o >= 0; o--) {
          if (structure.language[o].code == tmp_conf[i]) {
            out.push(structure.language.splice(o, 1)[0]);
            //console.log('Language moved ' + tmp_conf[i]);
          }
        }
      }
      out = out.concat(structure.language);
      structure.language = out;
      this.legacyLogService.add('Language list reordered'); // TODO legacy logger
    }
    return structure;
  }

  private filterContentByDevice(structure: Structure) {
    this.filterDeviceAwareCollection(structure.menu);
    this.filterDeviceAwareCollection(structure.pages);
    this.filterDeviceAwareCollection(structure.dynamicWelcome);

    for (const index in structure.pages) {
      if (structure.pages[index].type === 'list') {
        this.filterDeviceAwareCollection(structure.pages[index].listItems);
      }
    }

    return structure;
  }

  private filterDeviceAwareCollection<T extends {devices?: string[]}>(
    deviceAwareCollection: Record<string, T> | T[] | undefined,
  ) {
    if (!deviceAwareCollection) {
      return;
    }

    if (Array.isArray(deviceAwareCollection)) {
      for (let i = deviceAwareCollection.length - 1; i >= 0; i--) {
        if (!this.isDeviceAllowed(deviceAwareCollection[i].devices)) {
          deviceAwareCollection.splice(i, 1);
        }
      }
      return;
    }

    for (const key in deviceAwareCollection) {
      if (!this.isDeviceAllowed(deviceAwareCollection[key].devices)) {
        delete deviceAwareCollection[key];
      }
    }
  }

  private isDeviceAllowed(devices: string[] | undefined) {
    const device = 'tv';
    return !devices || !devices.length || devices.includes(device) || devices.includes('');
  }

  /**
   * Insert guest naming placeholders
   */
  private insertGuestPlaceholders(structure: Structure) {
    // Вставка placeholder для именного приветствия гостя
    structure.welcome.title = structure.welcome.title?.replaceAll(
      '{username}',
      '<span class="tv_welcome_greeting_placeholder"></span>',
    );
    structure.welcome.content = structure.welcome.content?.replaceAll(
      '{username}',
      '<span class="tv_welcome_greeting_placeholder"></span>',
    );

    if (structure.dynamicWelcome?.length) {
      structure.dynamicWelcome.forEach((item) => {
        item.title =
          item.title?.replaceAll(`{username}`, `<span class="tv_welcome_greeting_placeholder"></span>`) || '';
        item.content =
          item.content?.replaceAll(`{username}`, `<span class="tv_welcome_greeting_placeholder"></span>`) || '';
      });
    }

    return structure;
  }
}

declare global {
  interface Window {
    /**
     * @deprecated
     */
    structv2: Structure | null | undefined;

    /**
     * @deprecated
     */
    tv_content_url: string;

    /**
     * @deprecated
     */
    getPageFromStruct: StructService['getPageFromStruct'];

    /**
     * @deprecated
     */
    getRcuPageFromStruct: StructService['getRcuPageFromStruct'];
  }

  /**
   * @deprecated
   */
  const structv2: Structure | null | undefined;

  /**
   * @deprecated
   */
  const tv_content_url: string;

  /**
   * @deprecated
   */
  const getPageFromStruct: StructService['getPageFromStruct'];

  /**
   * @deprecated
   */
  const getRcuPageFromStruct: StructService['getRcuPageFromStruct'];
}
